<template>
    <div class="col-md-10 mx-auto">
        <div class="card">
            <div class="card-header bg-primary text-white">
                <page-title :title="$tc('administration.user.title',2)"></page-title>
            </div>
        </div>

        <div class="card">
            <div class="card-body">
                <tabs class="mt-2" :options="{ useUrlFragment: false, loadSavedTab: true}"
                      cache-lifetime="10"
                      @changed="tabChanged">

                    <tab class="pt-0" id="personInfoTab" :name="$t('person.title')">
                        <div class="card-body" v-if="personInfoTabActive">
                            <div v-if="userModel != null">
                                <user-details-card v-model="userModel"
                                                   :allowEdit="hasUpdateRights"
                                                   @userEdit="onUserEdit">
                                </user-details-card>
                            </div>

                            <div class="border border-success p-2">
                                <div class="form-group">
                                    <h4>{{ $t('administration.user.relatedPerson') }}</h4>
                                </div>
                                <person-details-card v-if="masterPersonModel && hasReadRights"
                                                     v-model="masterPersonModel"
                                                     :allowEdit="false"
                                                     :hideBtns="true"
                                                     :hideFooter="false"
                                                     :isAdministration="true">
                                </person-details-card>

                                <div v-if="hasUpdateRights" class="btn-group" role="group" aria-label="Master person management">
                                    <button v-if="masterPersonModel" type="button" class="btn btn-danger" @click.stop="removeMasterPerson">{{ $t('buttons.delete.btnText') }}</button>
                                    <button type="button" class="btn btn-primary" @click.stop="selectMasterPerson">{{ $t('buttons.load.btnText') }}</button>
                                    <button type="button" class="btn btn-success" @click.stop="createMasterPerson">{{ $t('buttons.createAndLoad.btnText') }}</button>
                                </div>

                                <div v-if="hasUpdateRights" class="alert alert-info m-2" role="alert">
                                    <ul class="ml-2">
                                        <li><span>{{ $t('userManagement.info.li1') }}</span></li>
                                        <li><span>{{ $t('userManagement.info.li2') }}</span></li>
                                        <li><span>{{ $t('userManagement.info.li3') }}</span></li>
                                        <li><span>{{ $t('userManagement.info.li4') }}</span></li>
                                    </ul>
                                </div>
                            </div>

                            <hr />
                            <h4>Обединени профили</h4>
                            <div class="form-group">
                                <div class="card-deck" v-if="mergedPersons">
                                    <person-details-card v-for="(p) in mergedPersons"
                                                         :key="p.id"
                                                         :value="p"
                                                         :hideBtns="false"
                                                         :hideFooter="false"
                                                         :isAdministration="true"
                                                         :mergePerson="hasUpdateRights || hasCreateRights"
                                                         @onMergeRemove="onMergeRemove">
                                    </person-details-card>
                                </div>
                            </div>

                            <div class="form-group">
                                <div v-if="masterPersonModel">
                                    <button type="button" class="btn btn-lg btn-success" @click.stop="selectPerson">
                                        <i class="far fa-plus-square text-success mr-2"></i>
                                        {{$t('buttons.add.btnText')}}
                                    </button>
                                </div>
                                <div v-else>
                                    <h4 class="text-warning">{{ $t('administration.user.missingRelatedPerson') }}</h4>
                                </div>
                            </div>
                        </div>
                    </tab>

                    <tab class="pt-0" id="claimsInfoTab" :name="$t('claims.title')">
                        <div v-if="claimsInfoTabActive && userId" class="form-group">
                            <UserClaimsDetails :userId="userId"
                                               :showCurrentUserLoadedClaims="false">
                            </UserClaimsDetails>
                        </div>
                    </tab>

                    <!--<tab class="pt-0" id="settingsTab" :name="$t('buttons.settings.btnText')">
                        <div v-if="settingsTabActive" class="form-group">
                            <vs-checkbox v-model="includeMergedProfiles"
                                         :name="'includeMergedProfiles' + _uid"
                                         :id="'includeMergedProfiles' + _uid"
                                         style="justify-content: flex-start !important">
                                {{ $t('administration.person.searchFormMerged') }}
                            </vs-checkbox>
                        </div>
                    </tab>-->

                </tabs>
            </div>
        </div>

        <PersonSelectorModal :show="showPersonSelector" :showId="true" :includeMergedProfiles.sync="includeMergedProfiles" @onSelect="onPersonSelect" @onClose="onPersonSelectorClose"></PersonSelectorModal>
        <PersonSelectorModal :show="showMasterPersonSelector" :showId="true" :includeMergedProfiles.sync="includeMergedProfiles" @onSelect="onMasterPersonSelect" @onClose="onMasterPersonSelectorClose"></PersonSelectorModal>
        <PersonCreateModal :show="showCreatePersonSelector" @onCreate="onPersonCreate" @onClose="onPersonCreatorClose"></PersonCreateModal>
    </div>

</template>

<script>
    import Vue from 'vue';
    import CustomTabs from '@/components/tab/CustomTabs'
    import PageTitle from '@/components/common/PageTitle';
    import userService from '@/services/user.service';
    import personService from '@/services/person.service';
    import actionLogService from "@/services/actionLog.service"
    import { AspNetUser, Person } from '@/models/models';
    import UserDetailsCard from '@/components/user/UserDetailsCard'
    import PersonSelectorModal from '@/components/person/selector.modal'
    import PersonDetailsCard from '@/components/person/PersonDetailsCard';
    import PersonCreateModal from '@/components/person/create.modal';
    import UserClaimsDetails from '@/components/administration/UserClaimsDetails';

    Vue.use(CustomTabs);

    export default {
        props: {
            id: {
                type: String,
                required: true
            }
        },
        components: {
            PageTitle,
            UserDetailsCard,
            PersonSelectorModal,
            PersonDetailsCard,
            PersonCreateModal,
            UserClaimsDetails
        },
        data() {
            return {
                rating: '',
                userModel: null,
                masterPersonModel: null,
                showMasterPersonSelector: false,
                showPersonSelector: false,
                mergedPersons: [],
                selectedPersons: [],
                showCreatePersonSelector: false,
                personInfoTabActive: false,
                claimsInfoTabActive: false,
                settingsTabActive: false,
                includeMergedProfiles: false
            }
        },
        methods: {
            loadUser(id) {
                userService
                    .getById(id)
                    .then(response => {
                        let model = response.data;
                        if (model) {
                            this.userModel = new AspNetUser(model);
                        } else {
                            this.userModel = null;
                        }

                        actionLogService.userManagementOpen(id, this.userModel ? JSON.stringify(this.model) : '');
                    })
                    .catch(error => {
                        this.userModel = null;

                        this.$vs.notify({
                            title: this.$t('notifications.error'),
                            text: this.$t('notifications.errorLoadingData'),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            clearMergedPersonsArr() {
                if (Array.isArray(this.mergedPersons) && this.mergedPersons.length > 0) {
                    while (this.mergedPersons.length > 0) {
                        this.mergedPersons.pop();
                    }
                }
            },
            loadRelatedPerson(userId) {
                this.loadMasterPersonForUser(userId);
                this.loadChildPersonForUser(userId);
            },
            loadMasterPersonForUser(id) {
                personService
                    .getByUserId(id)
                    .then(response => {
                        let model = response.data;
                        if (model) {
                            this.masterPersonModel = new Person(model);
                        } else {
                            this.masterPersonModel = null;
                        }
                    })
                    .catch(error => {
                        this.masterPersonModel = null;

                        this.$vs.notify({
                            title: this.$t('notifications.error'),
                            text: this.$t('notifications.errorLoadingData'),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            loadChildPersonForUser(id) {
                personService
                    .getChilderByUserId(id)
                    .then(response => {
                        let models = response.data;
                        if (models) {
                            this.clearMergedPersonsArr();

                            models.forEach(person => {
                                this.mergedPersons.push(new Person(person))
                            });
                        }
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('notifications.error'),
                            text: this.$t('notifications.errorLoadingData'),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            onPersonSelectorClose() {
                this.showPersonSelector = false;
            },
            onPersonSelect(selectedPerson) {
                if (selectedPerson) {
                    this.mergePersonToMaster(selectedPerson)
                }

                this.showPersonSelector = false;
            },
            selectPerson() {
                this.showPersonSelector = true;
            },
            removeMasterPerson() {
                if (!this.masterPersonModel) {
                    return;
                }

                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                kendo.confirm(`${this.$t('administration.user.confirmations.relatedPersonDelete')} "${this.masterPersonModel.fullName}"?`)
                    .then(function () {
                        userService
                            .removeMasterPerson(this.userModel.id, this.masterPersonModel.id)
                            .then(response => {
                                this.$vs.notify({
                                    title: this.$t('notifications.success'),
                                    text: this.$t('notifications.successfulSavedData'),
                                    color: 'success',
                                    time: this.$store.getters.notificationDuration,
                                })

                                this.loadRelatedPerson(this.userModel.id);
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('notifications.error'),
                                    text: this.$t('notifications.errorDataNotSaved'),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                })
                            });
                    }.bind(this));
            },
            selectMasterPerson() {
                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                this.showMasterPersonSelector = true;
            },
            onMasterPersonSelectorClose() {
                this.showMasterPersonSelector = false;
            },
            onMasterPersonSelect(selectedPerson) {
                if (!selectedPerson) {
                    return;
                }

                kendo.confirm(`${this.$t('administration.user.confirmations.relatedPersonAdd')} "${selectedPerson.fullName}"?`)
                    .then(function () {
                        this.showMasterPersonSelector = false;

                        userService
                            .addMasterPerson(this.userModel.id, selectedPerson.id)
                            .then(response => {
                                this.$vs.notify({
                                    title: this.$t('notifications.success'),
                                    text: this.$t('notifications.successfulSavedData'),
                                    color: 'success',
                                    time: this.$store.getters.notificationDuration,
                                })

                                this.loadRelatedPerson(this.userModel.id);
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('notifications.error'),
                                    text: this.$t('notifications.errorDataNotSaved'),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                })
                            });
                    }.bind(this));
            },
            onUserEdit() {
                this.reloadPage();
            },
            reloadPage() {
                window.location.reload(true);
            },
            createMasterPerson() {
                if (!this.hasCreateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                this.showCreatePersonSelector = true;
            },
            onPersonCreatorClose() {
                this.showCreatePersonSelector = false;
            },
            onPersonCreate(model) {
                if (!model) {
                    return;
                }

                kendo.confirm(`${this.$t('administration.user.confirmations.relatedPersonAdd')} "${model.fullName}"?`)
                    .then(function () {
                        this.showCreatePersonSelector = false;

                        userService
                            .addMasterPerson(this.userModel.id, model.id)
                            .then(response => {
                                this.$vs.notify({
                                    title: this.$t('notifications.success'),
                                    text: this.$t('notifications.successfulSavedData'),
                                    color: 'success',
                                    time: this.$store.getters.notificationDuration,
                                })

                                this.loadRelatedPerson(this.userModel.id);
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('notifications.error'),
                                    text: this.$t('notifications.errorDataNotSaved'),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                })
                            });
                    }.bind(this));
            },
            onMergeRemove(model) {
                if (!model) {
                    return;
                }

                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                kendo.confirm(`${this.$t('administration.user.confirmations.mergedPersonRemove')} "${model.fullName}"?`)
                    .then(function () {
                        userService
                            .removeMergedPerson(model.id, this.masterPersonModel.id)
                            .then(response => {
                                this.$vs.notify({
                                    title: this.$t('notifications.success'),
                                    text: this.$t('notifications.successfulSavedData'),
                                    color: 'success',
                                    time: this.$store.getters.notificationDuration,
                                })

                                this.loadRelatedPerson(this.userModel.id);
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('notifications.error'),
                                    text: this.$t('notifications.errorDataNotSaved'),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                })
                            });
                    }.bind(this));
            },
            mergePersonToMaster(model) {
                if (!model) {
                    return;
                }

                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                kendo.confirm(`${this.$t('administration.user.confirmations.mergedPersonAdd')} "${model.fullName}"?`)
                    .then(function () {
                        userService
                            .mergedPerson(model.id, this.masterPersonModel.id)
                            .then(response => {
                                this.$vs.notify({
                                    title: this.$t('notifications.success'),
                                    text: this.$t('notifications.successfulSavedData'),
                                    color: 'success',
                                    time: this.$store.getters.notificationDuration,
                                })

                                this.loadRelatedPerson(this.userModel.id);
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('notifications.error'),
                                    text: this.$t('notifications.errorDataNotSaved'),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                })
                            });
                    }.bind(this));
            },
            tabChanged(selectedTab) {
                if (!selectedTab) {
                    return;
                }

                switch (selectedTab.tab.id) {
                    case 'personInfoTab':
                        if (this.personInfoTabActive === false) {
                            this.personInfoTabActive = true;
                        }
                        break
                    case 'claimsInfoTab':
                        if (this.claimsInfoTabActive === false) {
                            this.claimsInfoTabActive = true;
                        }
                        break;
                    case 'settingsTab':
                        if (this.settingsTabActive === false) {
                            this.settingsTabActive = true;
                        }
                        break;
                    default:
                        break
                }
            },
        },
        mounted() {
            this.loadUser(this.id);
        },
        watch: {
            userModel: function (val) {
                if (val) {
                    this.loadRelatedPerson(this.userModel.id);
                }
            }
        },
        computed: {
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
            hasReadRights() {
                return this.$store.getters.claims.aspNetUser.read;
            },
            hasUpdateRights() {
                return this.$store.getters.claims.aspNetUser.update;
            },
            hasCreateRights() {
                return this.$store.getters.claims.aspNetUser.create;
            },
            userId() {
                if (!this.userModel) return null;

                return this.userModel.id;
            }
        }
    }
</script>

<style scoped>
</style>
